<template>
    <div id="nav" :class="['container-fluid', { 'scrolled': isScrolled }]">
        <div class="container d-flex justify-content-between align-items-center h-100">
            <div>
                <a href="/">
                    <img
                        class="logo"
                        src="/images/logo.svg"
                    />
                </a>
            </div>
            <div class="nav-main">
                <ul v-if="show" class="d-flex flex-row h-100 list-unstyled align-items-center">
                    <NavItem
                        label="Assortiment"
                        :menu="assortMenu"
                        menuType="big"
                        :active="routeName === 'Assortiment' || routeName === 'Zalto' || routeName === 'Stoppers'"
                        @menuItemClicked="callback"></NavItem>
                    <NavItem
                        label="Acties"
                        styling="red"
                        :active="routeName === 'Actions'"
                        @navClick="navigateTo('Actions')"></NavItem>
                    <li class="d-flex align-items-center">
                        <BaseButton icon="search" text="Zoeken" @buttonClick="openSearch"/>
                    </li>
                    <NavItem
                        img="account"
                        label="Account"
                        :menu="accountMenu"
                        :active="routeName === 'Account' || routeName === 'Orders' || routeName === 'Invoices'"
                        :routeName="routeName"
                        @menuItemClicked="callback"></NavItem>
                    <NavItem
                        img="heart-outline"
                        label="Mijn wijnen"
                        :menu="wineMenu"
                        :active="routeName === 'Favorites' || routeName === 'OrderedBefore'"
                        :routeName="routeName"
                        @menuItemClicked="callback"></NavItem>
                </ul>
            </div>
            <div>
                <ul class="list-inline h-100 d-flex align-items-center mb-0">
                    <NavItem
                        v-if="show"
                        :img="cartCount >= 1 ? 'winkelmand_filled' : 'winkelmand_empty'"
                        label="Winkelmand"
                        :count="cartCount"
                        :active="routeName === 'Cart'"
                        @click="navigateTo('Cart')" />
                </ul>
            </div>
        </div>

        <teleport v-if="showModal" to="#modal">
            <Modal>
                <template v-slot:body>
                    <div class="d-flex flex-column justify-content-center align-items-center">
                        <h1 class="red">
                            Zoeken?
                        </h1>
                        <TextInput
                            ref="searchInput"
                            v-model="searchString"
                            type="text"
                            @submit="search"
                            @iconRightClick="search"
                        >
                            <template #icon-right>
                                <component :is="SearchIcon" class="icon"/>
                            </template>
                        </TextInput>
                    </div>
                </template>
                <template v-slot:backdrop>
                    <div class="modal-backdrop show"></div>
                </template>
            </Modal>
        </teleport>
    </div>
</template>

<script lang="ts">
import {
    computed, ref, watch, nextTick, onMounted, onUnmounted,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SearchIcon from '@/assets/icons/search-outline.svg';
import NavItem from '@/components/NavItem.vue';
import BaseButton from '@/shared/components/BaseButton.vue';
import TextInput from '@/shared/components/form/TextInput.vue';
import {
    useAuthStore, useModalStore, useCartStore, useUIStore, 
} from '@/stores';
import Modal from '@/views/Modal.vue';

export default {
    components: {
        TextInput,
        BaseButton,
        NavItem,
        Modal,
    },
    props: {
        show: {
            type: Boolean,
        },
    },
    setup() {
        const searchInput: any = ref(null);
        const authStore = useAuthStore();
        const modalStore = useModalStore();
        const cartStore = useCartStore();
        const uiStore = useUIStore();
        const router = useRouter();
        const showModal = computed(() => modalStore.showModal('search'));
        const cartCount = computed(() => cartStore.cartCount);
        const searchString = ref(modalStore.getSearch);
        const isScrolled = computed(() => uiStore.getIsScrolled);

        onMounted(() => {
            uiStore.initScrollListener();
        });

        onUnmounted(() => {
            uiStore.removeScrollListener();
        });

        function logout() {
            authStore.logout().then(() => router.push({ name: 'Login' }));
        }

        function navigateTo(url: string, query?: any) {
            window.scrollTo(0, 0);
            router.push({ name: url, query });
        }

        const accountMenu = [
            {
                img: 'account',
                label: 'Accountoverzicht',
                routeName: 'Account',
                callBack: () => navigateTo('Account'),
            },
            {
                img: 'orders',
                label: 'Bestellingen',
                routeName: 'Orders',
                callBack: () => navigateTo('Orders'),
            },
            {
                img: 'invoices',
                label: 'Facturen',
                routeName: 'Invoices',
                callBack: () => navigateTo('Invoices'),
            },
            {
                img: 'logout',
                label: 'Uitloggen',
                callBack: logout,
            },
        ];

        const wineMenu = [
            {
                label: '',
                callBack: () => null,
            },
            {
                img: 'ordered-before',
                label: 'Eerder besteld',
                routeName: 'OrderedBefore',
                callBack: () => navigateTo('OrderedBefore'),
            },
            {
                img: 'heart-outline',
                label: 'Favorieten',
                routeName: 'Favorites',
                callBack: () => navigateTo('Favorites'),
            },
            {
                label: '',
                callBack: () => null,
            },
        ];

        const assortMenu = [
            {
                label: 'ALLE WIJNLANDEN',
                callBack: () => navigateTo('Assortiment'),
            },
            {
                label: 'Frankrijk',
                callBack: () => navigateTo('Assortiment', { land: 'frankrijk' }),
            },
            {
                label: 'Portugal',
                callBack: () => navigateTo('Assortiment', { land: 'portugal' }),
            },
            {
                label: 'Spanje',
                callBack: () => navigateTo('Assortiment', { land: 'spanje' }),
            },
            {
                label: 'Italië',
                callBack: () => navigateTo('Assortiment', { land: 'italië' }),
            },
            {
                label: 'Luxemburg',
                callBack: () => navigateTo('Assortiment', { land: 'luxemburg' }),
            },
            {
                label: 'Duitsland',
                callBack: () => navigateTo('Assortiment', { land: 'duitsland' }),
            },
            {
                label: 'Oostenrijk',
                callBack: () => navigateTo('Assortiment', { land: 'oostenrijk' }),
            },
            {
                label: 'Verenigde Staten',
                callBack: () => navigateTo('Assortiment', { land: 'verenigde staten' }),
            },
            {
                label: 'Chili',
                callBack: () => navigateTo('Assortiment', { land: 'chili' }),
            },
            {
                label: 'Argentinië',
                callBack: () => navigateTo('Assortiment', { land: 'argentinië' }),
            },
            {
                label: 'Nieuw-Zeeland',
                callBack: () => navigateTo('Assortiment', { land: 'nieuw-zeeland' }),
            },
            {
                label: 'Australië',
                callBack: () => navigateTo('Assortiment', { land: 'australië' }),
            },
            {
                label: 'Zuid-Afrika',
                callBack: () => navigateTo('Assortiment', { land: 'zuid-afrika' }),
            },
            {
                label: '',
                callBack: () => null,
            },
            {
                label: 'WIJNSTOPPER',
                callBack: () => navigateTo('Stoppers'),
            },
            {
                label: 'ZALTO GLAS',
                callBack: () => navigateTo('Zalto'),
            },
        ];

        const route = useRoute();
        const routeName = ref(route.name as string);

        watch(route, () => {
            routeName.value = route.name as string;
        });

        function callback(val: any) {
            if (val.callBack) {
                val.callBack();
            }
        }

        function openSearch() {
            modalStore.showModalAction('search');
            searchString.value = '';

            nextTick(() => {
                if (searchInput.value && searchInput.value !== null && searchInput.value.$el) {
                    const input = searchInput.value.$el.firstElementChild?.children?.item(1);
                    if (input && input instanceof HTMLElement) {
                        input.focus();
                    }
                }
            });
        }

        function search() {
            modalStore.hideModal();
            modalStore.setSearch(searchString.value);
            navigateTo('Assortiment', { search: searchString.value });
            modalStore.doSearch();
        }

        return {
            accountMenu,
            wineMenu,
            assortMenu,
            showAccount: ref(false),
            showOther: ref(false),
            callback,
            openSearch,
            search,
            showModal,
            navigateTo,
            SearchIcon,
            searchString,
            cartCount,
            routeName,
            searchInput,
            isScrolled,
        };
    },
};
</script>

<style scoped lang="scss">
@import "~@/assets/variables";

#nav {
    height: 100px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    box-shadow: 0px 0px 10px 0px #D9D9D9;
    background: white;
    transition: height 0.3s ease;

    &.scrolled {
        height: 70px;
    }
}

.icon {
    width: 50%;
}

* {
    user-select: none;
}

.logo {
    height: 45px;
    transition: height 0.3s ease;

    #nav.scrolled & {
        height: 35px;
    }
}

.nav-main {
    flex: none;

    ul {
        margin: 0;

        li {
            button {
                width: 130px;
                height: 30px;
                margin: 0 10px;
                border: none;
                &:focus {
                    outline: none;
                }
            }
        }
    }
}

.input-container {
    width: 30vw;
}
.modal-backdrop {
    background: $white;

    &.show {
        opacity: .95;
    }
}

@media only screen and (max-width: 1199px) {
    #nav {
        position: unset;
        height: auto;
        
        &.scrolled {
            height: auto;
        }
    }
    
    .logo {
        height: 59px;
        
        #nav.scrolled & {
            height: 59px;
        }
    }
    
    .nav-main {
        position: fixed;
        width: 100%;
        height: 100vh;
        background: white;
        max-height: none;
        max-width: none;
        top: 0;
        left: 0;
        z-index: 10;
        border: 15px solid $kwast-red;
        padding: 96px 71px;

        ul {
            flex-direction: column !important;

            li {
                height: 32px !important;
                margin-bottom: 45px;

                &:nth-of-type(3) {
                    order: 1;
                }
            }
        }

    }
}
</style>
